<script setup lang="ts">
import { computed, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Toaster } from 'vue-sonner'
import ConfirmDialogHost from '@/components/ui/ConfirmDialogHost.vue'
import { useRouteProgress } from '@/composables/useRouteProgress'
import { useLocaleDir } from '@/composables/useLocale'
import { useReducedMotion } from '@/composables/useReducedMotion'

const route = useRoute()
const { t, locale } = useI18n()
useLocaleDir()

const title = computed(() => {
  const key = route.meta.titleKey as string | undefined
  return key ? t(key) : t('meta.home')
})

watch(
  [title, locale],
  () => {
    document.title = title.value
  },
  { immediate: true },
)

const { progress, visible } = useRouteProgress()
const { prefersReducedMotion } = useReducedMotion()
</script>

<template>
  <a
    href="#main-content"
    class="sr-only focus:not-sr-only focus:absolute focus:start-4 focus:top-4 focus:z-[200] focus:rounded-lg focus:bg-primary-600 focus:px-4 focus:py-2 focus:text-white"
  >
    Skip to content
  </a>
  <div
    class="pointer-events-none fixed start-0 top-0 z-[150] h-0.5 w-full bg-transparent"
    :class="{ 'opacity-0': !visible }"
    aria-hidden="true"
  >
    <div
      class="h-full bg-gradient-to-r from-primary-400 to-primary-600 transition-[width] duration-200 ease-out"
      :style="{ width: `${progress}%` }"
    />
  </div>
  <RouterView v-slot="{ Component }">
    <main id="main-content" class="min-h-0 flex-1 outline-none" tabindex="-1">
      <Transition v-if="!prefersReducedMotion" name="page-fade" mode="out-in">
        <component :is="Component" />
      </Transition>
      <component :is="Component" v-else />
    </main>
  </RouterView>
  <Toaster position="top-center" rich-colors close-button />
  <ConfirmDialogHost />
</template>

<style>
.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.18s ease;
}
.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}
</style>
